/**
 * Calendar arithmetic for the planning screen. Pure functions, no dependency.
 *
 * Everything here works in the viewer's local timezone and speaks `YYYY-MM-DD`
 * strings, the same shape the API stores. `toIsoDate` builds that string from
 * the local calendar fields rather than `toISOString()`, which converts to UTC
 * first and hands back yesterday for anyone west of Greenwich after 5pm — the
 * same reason `lib/format.ts` parses `${value}T00:00:00` instead of `${value}`.
 */

/** A month, decomposed. `month` is 1-12, not the `Date` 0-11. */
export interface MonthKey {
  year: number;
  month: number;
}

/** One cell of the month grid. */
export interface CalendarDay {
  /** `YYYY-MM-DD`. */
  iso: string;
  /** False for the leading and trailing days borrowed from the neighbouring months. */
  inMonth: boolean;
  isToday: boolean;
}

/** `YYYY-MM`, months 01-12 only. */
const MONTH_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;

const MS_PER_DAY = 86_400_000;

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

/** The local calendar date of a `Date`, as `YYYY-MM-DD`. Never via UTC. */
export function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Midnight local on a `YYYY-MM-DD`. Invalid input yields an invalid `Date`. */
export function parseIsoDate(iso: string): Date {
  return new Date(`${iso}T00:00:00`);
}

/** Today, in the viewer's timezone. */
export function todayIso(): string {
  return toIsoDate(new Date());
}

/** `YYYY-MM` of a `YYYY-MM-DD`. */
export function monthOf(iso: string): string {
  return iso.slice(0, 7);
}

export function formatMonth(key: MonthKey): string {
  return `${key.year}-${pad(key.month)}`;
}

/** A `YYYY-MM` string, or null when it is not one. */
export function parseMonth(value: string | null | undefined): MonthKey | null {
  if (!value) return null;
  const match = MONTH_PATTERN.exec(value);
  if (match === null) return null;
  return { year: Number(match[1]), month: Number(match[2]) };
}

/** The month showing right now, as `YYYY-MM`. */
export function currentMonth(): string {
  const now = new Date();
  return formatMonth({ year: now.getFullYear(), month: now.getMonth() + 1 });
}

/** `YYYY-MM` shifted by whole months. Rolls the year over in both directions. */
export function addMonths(month: string, delta: number): string {
  const key = parseMonth(month) ?? parseMonth(currentMonth());
  if (key === null) return month;
  // Date normalises out-of-range months, so December + 1 becomes next January.
  const shifted = new Date(key.year, key.month - 1 + delta, 1);
  return formatMonth({ year: shifted.getFullYear(), month: shifted.getMonth() + 1 });
}

/** How many days the month has. */
export function daysInMonth(month: string): number {
  const key = parseMonth(month);
  if (key === null) return 30;
  // Day 0 of the next month is the last day of this one.
  return new Date(key.year, key.month, 0).getDate();
}

/** First day of the month, `YYYY-MM-DD`. */
export function monthStart(month: string): string {
  return `${month}-01`;
}

/** Last day of the month, `YYYY-MM-DD`. */
export function monthEnd(month: string): string {
  return `${month}-${pad(daysInMonth(month))}`;
}

/** True when a `YYYY-MM-DD` falls inside a `YYYY-MM`. */
export function isInMonth(iso: string, month: string): boolean {
  return monthOf(iso) === month;
}

/** `YYYY-MM-DD` shifted by whole days. */
export function addDays(iso: string, delta: number): string {
  const date = parseIsoDate(iso);
  date.setDate(date.getDate() + delta);
  return toIsoDate(date);
}

/**
 * Every date from `startIso` to `endIso` inclusive. Capped at `limit` days so a
 * mistyped multi-year range cannot lock the render up.
 */
export function datesBetween(startIso: string, endIso: string, limit = 400): string[] {
  const start = parseIsoDate(startIso);
  const end = parseIsoDate(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];

  const span = Math.min(Math.round((end.getTime() - start.getTime()) / MS_PER_DAY), limit - 1);
  const dates: string[] = [];
  for (let offset = 0; offset <= span; offset += 1) dates.push(addDays(startIso, offset));
  return dates;
}

/**
 * The month laid out as full Monday-first weeks. Leading and trailing days come
 * from the neighbouring months and are marked `inMonth: false`, so the grid is
 * always a whole number of seven-day rows.
 *
 * `today` is a parameter rather than a call to the clock so the function stays
 * pure and testable.
 */
export function buildMonthGrid(month: string, today: string = todayIso()): CalendarDay[][] {
  const key = parseMonth(month) ?? parseMonth(currentMonth());
  if (key === null) return [];

  const first = new Date(key.year, key.month - 1, 1);
  // getDay() is 0 for Sunday; Monday-first wants Monday at 0.
  const lead = (first.getDay() + 6) % 7;
  const total = lead + daysInMonth(formatMonth(key));
  const weekCount = Math.ceil(total / 7);

  const weeks: CalendarDay[][] = [];
  for (let week = 0; week < weekCount; week += 1) {
    const days: CalendarDay[] = [];
    for (let index = 0; index < 7; index += 1) {
      const date = new Date(key.year, key.month - 1, 1 - lead + week * 7 + index);
      const iso = toIsoDate(date);
      days.push({
        iso,
        inMonth: date.getMonth() === key.month - 1 && date.getFullYear() === key.year,
        isToday: iso === today,
      });
    }
    weeks.push(days);
  }
  return weeks;
}

/**
 * Weekday headings, Monday first, in the viewer's locale. Read off a week that
 * is known to start on a Monday (2024-01-01) rather than hard-coded English.
 */
export const WEEKDAY_LABELS: readonly string[] = Array.from({ length: 7 }, (_unused, index) =>
  new Date(2024, 0, 1 + index).toLocaleDateString(undefined, { weekday: 'short' }),
);

/** `M T W T F S S` — the phone's weekday heads, Monday first like the grid. */
export const WEEKDAY_NARROW_LABELS: readonly string[] = Array.from(
  { length: 7 },
  (_unused, index) =>
    new Date(2024, 0, 1 + index).toLocaleDateString(undefined, { weekday: 'narrow' }),
);

/** `August 2026` — the toolbar's month label. */
export function monthLabel(month: string): string {
  const parsed = parseIsoDate(monthStart(month));
  if (Number.isNaN(parsed.getTime())) return month;
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
}

/** `Aug 2026` — the month as a tag beside something else, not as a heading. */
export function shortMonthLabel(month: string): string {
  const parsed = parseIsoDate(monthStart(month));
  if (Number.isNaN(parsed.getTime())) return month;
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
}

/** `August` — for copy that reads as a sentence. */
export function monthName(month: string): string {
  const parsed = parseIsoDate(monthStart(month));
  if (Number.isNaN(parsed.getTime())) return month;
  return parsed.toLocaleDateString(undefined, { month: 'long' });
}

/** `Mon 04 Aug` — a day heading in the list view. */
export function dayLabel(iso: string): string {
  const parsed = parseIsoDate(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(undefined, {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
}

/** `HH:MM`, 24-hour, as a `<input type="time">` speaks it. */
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * The instant a local `YYYY-MM-DD` and `HH:MM` name, as ISO-8601 **with the
 * viewer's offset**. This is what `publish_at` carries: the server stores an
 * instant and never needs to know a timezone, because the browser composing
 * the string is standing in one. An unparsable pair yields null.
 */
export function toInstant(iso: string, time: string): string | null {
  const match = TIME_PATTERN.exec(time);
  const date = parseIsoDate(iso);
  if (match === null || Number.isNaN(date.getTime())) return null;
  date.setHours(Number(match[1]), Number(match[2]), 0, 0);
  // Local fields, not toISOString(): that would emit UTC and lose the offset
  // the user's clock was set in.
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absolute = Math.abs(offsetMinutes);
  const offset = `${sign}${pad(Math.floor(absolute / 60))}:${pad(absolute % 60)}`;
  return `${toIsoDate(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}:00${offset}`;
}

/** The local `HH:MM` of an instant, for the time field. Null if unparsable. */
export function localTimeOf(instant: string): string | null {
  const parsed = new Date(instant);
  if (Number.isNaN(parsed.getTime())) return null;
  return `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

/** `09:00` or `9:00 AM`, whichever the viewer's locale says. */
export function timeLabel(instant: string): string {
  const parsed = new Date(instant);
  if (Number.isNaN(parsed.getTime())) return instant;
  return parsed.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
